import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, CircularProgress, Paper, Chip, Button, Tooltip
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { pasesTimelineJWT } from '../../api/endpoints';
import { capitalizar } from '../../utils/capitalizar';
import EnviarPaseDialog from './EnviarPaseDialog';

const accionColor = {
    aceptado: '#4caf50',
    rechazado: '#f44336',
    en_transito: '#ff9800',
    enviado: '#2196f3',
};

function PasesTab({ idExpediente, onUbicacionChange, refreshKey, ubicacion, puedeEnviar = true, motivoNoEnviar = '' }) {
    const [pases, setPases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);

    const cargar = useCallback(async () => {
        try {
            const resp = await pasesTimelineJWT(idExpediente);
            if (resp?.ok) setPases(resp.pases || []);
        } catch { }
        finally { setLoading(false); }
    }, [idExpediente]);

    useEffect(() => { cargar(); }, [cargar]);

    // Recargar cuando se envía desde fuera (ubicación header)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (refreshKey > 0) cargar(); }, [refreshKey]);

    // Polling: recargar timeline + ubicación cada 30s
    useEffect(() => {
        const interval = setInterval(() => {
            cargar();
            onUbicacionChange?.();
        }, 30000);
        return () => clearInterval(interval);
    }, [cargar, onUbicacionChange]);

    if (loading) {
        return <Box display="flex" justifyContent="center" py={6}><CircularProgress size={32} /></Box>;
    }

    const getEstadoPase = (pase) => {
        if (pase.destinoAccion === 3) return 'aceptado';
        if (pase.destinoAccion === 4) return 'rechazado';
        return 'en_transito';
    };

    const getEstadoLabel = (estado) => {
        if (estado === 'aceptado') return 'Aceptado';
        if (estado === 'rechazado') return 'Rechazado';
        return 'Pendiente';
    };

    const getEstadoChipColor = (estado) => {
        if (estado === 'aceptado') return 'success';
        if (estado === 'rechazado') return 'error';
        return 'warning';
    };

    const getPrioridadColor = (prioridad) => {
        if (prioridad >= 3) return 'error';
        if (prioridad === 2) return 'warning';
        return 'default';
    };

    return (
        <Box>
            {/* Card de ubicación actual */}
            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    mb: 2.5,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: ubicacion
                        ? ubicacion.estado === 'aceptado' ? '#e8f5e9' : ubicacion.estado === 'en_transito' ? '#fff8e1' : '#fafafa'
                        : '#fafafa',
                    borderColor: ubicacion
                        ? ubicacion.estado === 'aceptado' ? '#4caf50' : ubicacion.estado === 'en_transito' ? '#ff9800' : '#e0e0e0'
                        : '#e0e0e0',
                }}
            >
                <Box display="flex" alignItems="center" gap={1.5}>
                    <LocationOnIcon sx={{ color: ubicacion?.estado === 'aceptado' ? '#4caf50' : ubicacion?.estado === 'en_transito' ? '#ff9800' : '#999' }} />
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {ubicacion
                                ? ubicacion.estado === 'en_transito'
                                    ? `En tránsito → ${capitalizar(ubicacion.usuarioNombre)}`
                                    : capitalizar(ubicacion.usuarioNombre)
                                : 'Sin ubicación registrada'
                            }
                        </Typography>
                        {ubicacion && (
                            <Typography variant="caption" color="text.secondary">
                                {ubicacion.estado === 'aceptado' ? 'Aceptado' : ubicacion.estado === 'en_transito' ? 'Pendiente de aceptación' : 'Rechazado'} — {ubicacion.fecha} {ubicacion.hora}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Tooltip title={puedeEnviar ? '' : motivoNoEnviar} arrow>
                    <span>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<SendIcon />}
                            onClick={() => setDialogOpen(true)}
                            disabled={!puedeEnviar}
                            sx={{ textTransform: 'none', borderRadius: 1 }}
                        >
                            Enviar
                        </Button>
                    </span>
                </Tooltip>
            </Paper>

            {/* Contador */}
            {pases.length > 0 && (
                <Typography variant="subtitle2" color="text.secondary" mb={1.5}>
                    {pases.length} pase{pases.length !== 1 ? 's' : ''} registrado{pases.length !== 1 ? 's' : ''}
                </Typography>
            )}

            {pases.length === 0 ? (
                <Box textAlign="center" py={4}>
                    <Typography variant="body1" sx={{ color: '#bbb' }}>
                        No hay pases registrados para este expediente
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ position: 'relative', pl: 3 }}>
                    {/* Línea vertical del timeline */}
                    <Box sx={{
                        position: 'absolute',
                        left: 11,
                        top: 8,
                        bottom: 8,
                        width: 2,
                        backgroundColor: '#e0e0e0',
                    }} />

                    {pases.map((pase, idx) => {
                        const estado = getEstadoPase(pase);
                        const color = accionColor[estado] || accionColor.enviado;
                        const isFirst = idx === 0;

                        return (
                            <Box key={pase.id} sx={{ position: 'relative', mb: 2 }}>
                                {/* Nodo del timeline */}
                                <Box sx={{
                                    position: 'absolute',
                                    left: -19,
                                    top: 14,
                                    width: 14,
                                    height: 14,
                                    borderRadius: '50%',
                                    backgroundColor: color,
                                    border: '2px solid #fff',
                                    boxShadow: isFirst ? `0 0 0 3px ${color}40` : 'none',
                                    zIndex: 1,
                                }} />

                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        borderRadius: 1,
                                        borderLeft: `3px solid ${color}`,
                                        ...(isFirst && estado === 'en_transito' ? {
                                            backgroundColor: '#fff8e1',
                                            borderColor: accionColor.en_transito,
                                        } : {}),
                                    }}
                                >
                                    {/* Header del pase */}
                                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="caption" color="text.secondary">
                                                {pase.origenFecha} {pase.origenHora}
                                            </Typography>
                                            <Chip
                                                label={getEstadoLabel(estado)}
                                                color={getEstadoChipColor(estado)}
                                                size="small"
                                                variant="outlined"
                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                            {pase.prioridad >= 2 && (
                                                <Chip
                                                    icon={<PriorityHighIcon sx={{ fontSize: 14 }} />}
                                                    label={pase.prioridadNombre}
                                                    color={getPrioridadColor(pase.prioridad)}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                />
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Origen → Destino */}
                                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {capitalizar(pase.origenUsuarioNombre || pase.origenUsuario)}
                                        </Typography>
                                        <ArrowForwardIcon sx={{ fontSize: 16, color: '#999' }} />
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {capitalizar(pase.destinoUsuarioNombre || pase.destinoUsuario)}
                                        </Typography>
                                    </Box>

                                    {/* Observaciones del envío */}
                                    {pase.origenObs && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', mb: 0.5 }}>
                                            "{pase.origenObs}"
                                        </Typography>
                                    )}

                                    {/* Respuesta del destinatario */}
                                    {estado === 'aceptado' && (
                                        <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                            <CheckCircleIcon sx={{ fontSize: 14, color: '#4caf50' }} />
                                            <Typography variant="caption" color="success.main">
                                                Aceptado {pase.destinoFecha} {pase.destinoHora}
                                                {pase.destinoObs ? ` — "${pase.destinoObs}"` : ''}
                                            </Typography>
                                        </Box>
                                    )}

                                    {estado === 'rechazado' && (
                                        <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                            <CancelIcon sx={{ fontSize: 14, color: '#f44336' }} />
                                            <Typography variant="caption" color="error.main">
                                                Rechazado {pase.destinoFecha} {pase.destinoHora}
                                                {pase.destinoObs ? ` — "${pase.destinoObs}"` : ''}
                                            </Typography>
                                        </Box>
                                    )}

                                    {estado === 'en_transito' && isFirst && (
                                        <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                            <AccessTimeIcon sx={{ fontSize: 14, color: '#ff9800' }} />
                                            <Typography variant="caption" color="warning.main">
                                                Esperando respuesta...
                                            </Typography>
                                        </Box>
                                    )}
                                </Paper>
                            </Box>
                        );
                    })}
                </Box>
            )}

            <EnviarPaseDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                idExpediente={idExpediente}
                onEnviado={() => { cargar(); onUbicacionChange?.(); }}
            />
        </Box>
    );
}

export default PasesTab;
